import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  OutlinedInput,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {useNavigate, useParams} from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import {useEffect, useMemo, useState} from 'react';
import {v4 as uuidv4} from 'uuid';
// import {accessGroupServiceDetail, accessGroupServiceSave} from 'src/apis';
import {
  accessGroupServiceDetail,
  accessGroupServiceSave,
  accessGroupServiceUpdate,
} from 'src/apis/autoGenerate/services';
import {useIntercomOptions, useOrgId} from 'src/hooks';
import {AutocompleteOption, AutocompleteVal} from 'src/components/SearchApartment';
// import {
//   AccessGroup,
//   AccessGroupIntercom,
//   AccessGroupIntercomWeekdaysEnum,
// } from 'src/apis/autoGenerate';
import {AccessGroup} from 'src/apis/autoGenerate/types/accessGroup';
import {AccessGroupIntercom} from 'src/apis/autoGenerate/types';
import {AccessGroupIntercomWeekdaysEnum} from 'src/apis/autoGenerate/types';
import {TimePicker} from '@mui/x-date-pickers/TimePicker';
import {UUID_PREFIX, WEEK_ITEMS} from 'src/const';
import {formatTime, reverseFormatTime} from 'src/utils/common';
import message from 'src/components/Message';
import Spin from 'src/components/Spin';
import {DataGrid, GridColDef} from '@mui/x-data-grid';
import {intercomDeviceServiceFind} from 'src/apis/autoGenerate/services';
import {IntercomDevice} from 'src/apis/autoGenerate/types/intercomDevice';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

function getInitIntercomVal(): AccessGroupIntercom {
  return {
    id: `${UUID_PREFIX}${uuidv4()}`,
    weekdays: [],
    allTimeAccess: false,
    timeStart: '',
    timeEnd: '',
  };
}

const steps = ['Name Access Group', 'Select Devices', 'Configure Access Times', 'Done'];

// Add this interface for the component props
interface DeviceSelectionTableProps {
  devices: IntercomDevice[];
  selectedDeviceIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  loading: boolean;
}

const DeviceSelectionTable = ({
  devices,
  selectedDeviceIds,
  onSelectionChange,
  loading,
}: DeviceSelectionTableProps) => {
  // Handle checkbox changes
  const handleCheckboxChange = (deviceId: string) => {
    if (selectedDeviceIds.includes(deviceId)) {
      onSelectionChange(selectedDeviceIds.filter(id => id !== deviceId));
    } else {
      onSelectionChange([...selectedDeviceIds, deviceId]);
    }
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox"></TableCell>
            <TableCell>Device ID</TableCell>
            <TableCell>Device Name</TableCell>
            <TableCell>Building</TableCell>
            <TableCell>Apartment</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {devices.map((device: IntercomDevice) => (
            <TableRow key={device.id}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedDeviceIds.includes(device.id || '')}
                  onChange={() => handleCheckboxChange(device.id || '')}
                />
              </TableCell>
              <TableCell>{device.id}</TableCell>
              <TableCell>{device.name}</TableCell>
              <TableCell>{device.building?.name}</TableCell>
              <TableCell>{device.apartment?.name}</TableCell>
              <TableCell>{device.status}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default function AddAccessGroup() {
  const {groupId} = useParams();
  const [intercoms, setIntercoms] = useState<AccessGroupIntercom[]>([]);
  const {orgId} = useOrgId();
  const [accessGroup, setAccessGroup] = useState<AccessGroup>({name: ''});
  const {intercomOptions} = useIntercomOptions();
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  // Step state
  const [activeStep, setActiveStep] = useState(0);

  // Device selection state
  const [devices, setDevices] = useState<IntercomDevice[]>([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);

  // Access time settings
  const [bulkAllTimeAccess, setBulkAllTimeAccess] = useState(false);
  const [bulkTimeStart, setBulkTimeStart] = useState<any>(null);
  const [bulkTimeEnd, setBulkTimeEnd] = useState<any>(null);
  const [bulkWeekdays, setBulkWeekdays] = useState<AccessGroupIntercomWeekdaysEnum[]>([]);

  useEffect(() => {
    if (groupId) {
      setSaving(true);
      accessGroupServiceDetail(groupId)
        .then(res => {
          // Make sure we're properly handling the response data structure
          const accessGroup = res.data || res;

          if (accessGroup) {
            const {intercoms, ...groupInfo} = accessGroup;
            setAccessGroup(groupInfo);

            if (intercoms) {
              setIntercoms(intercoms);
              // Extract selected device IDs
              const deviceIds = intercoms.map(i => i.intercomDeviceId).filter(Boolean) as string[];
              setSelectedDeviceIds(deviceIds);

              // Extract bulk settings from the first intercom
              if (intercoms.length > 0) {
                const firstIntercom = intercoms[0];
                setBulkAllTimeAccess(firstIntercom.allTimeAccess || false);
                if (firstIntercom.timeStart)
                  setBulkTimeStart(reverseFormatTime(firstIntercom.timeStart));
                if (firstIntercom.timeEnd) setBulkTimeEnd(reverseFormatTime(firstIntercom.timeEnd));
                setBulkWeekdays(firstIntercom.weekdays || []);
              }
            }
          }
        })
        .catch(err => {
          message.error(err.message);
        })
        .finally(() => {
          setSaving(false);
        });
    }
  }, [groupId]);

  // Load devices when entering step 2
  useEffect(() => {
    if (activeStep === 1 && devices.length === 0) {
      setLoadingDevices(true);
      intercomDeviceServiceFind(orgId)
        .then(res => {
          if (res.data && res.data.intercomDevices) {
            setDevices(res.data.intercomDevices);

            // If we're in edit mode and have device IDs from loaded intercoms,
            // make sure they remain selected after loading the devices
            if (groupId && selectedDeviceIds.length > 0) {
              console.log('Preserving selected devices:', selectedDeviceIds);
            }
          }
        })
        .catch(err => {
          message.error(err.message);
        })
        .finally(() => {
          setLoadingDevices(false);
        });
    }
  }, [activeStep, orgId, devices.length, groupId, selectedDeviceIds]);

  const handleSave = () => {
    if (!accessGroup.name) {
      message.warning('Access Group Name is required');
      return;
    }

    if (selectedDeviceIds.length === 0) {
      message.warning('You must select at least one device');
      return;
    }

    setSaving(true);

    // Convert selected device IDs to AccessGroupIntercom objects
    const finalIntercoms = selectedDeviceIds.map(deviceId => {
      const existing = intercoms.find(i => i.intercomDeviceId === deviceId);
      if (existing) {
        // Update existing intercom with new bulk time settings
        return {
          ...existing,
          allTimeAccess: bulkAllTimeAccess,
          timeStart: bulkTimeStart ? formatTime(bulkTimeStart.$d) : '',
          timeEnd: bulkTimeEnd ? formatTime(bulkTimeEnd.$d) : '',
          weekdays: [...bulkWeekdays],
        };
      }

      return {
        ...getInitIntercomVal(),
        intercomDeviceId: deviceId,
        allTimeAccess: bulkAllTimeAccess,
        timeStart: bulkTimeStart ? formatTime(bulkTimeStart.$d) : '',
        timeEnd: bulkTimeEnd ? formatTime(bulkTimeEnd.$d) : '',
        weekdays: [...bulkWeekdays],
      };
    });

    const requestData = {
      accessGroup: {...accessGroup, orgId},
      intercoms: finalIntercoms,
    };

    if (groupId) {
      // Update existing access group
      accessGroupServiceUpdate(groupId, requestData)
        .then(() => {
          setActiveStep(3);
        })
        .catch(err => {
          message.error(err.message);
        })
        .finally(() => {
          setSaving(false);
        });
    } else {
      // Create new access group
      accessGroupServiceSave(requestData)
        .then(() => {
          setActiveStep(3);
        })
        .catch(err => {
          message.error(err.message);
        })
        .finally(() => {
          setSaving(false);
        });
    }
  };

  const handleNext = () => {
    // Validation per step
    if (activeStep === 0 && !accessGroup.name) {
      message.warning('Please enter a name for the access group');
      return;
    }

    if (activeStep === 1 && selectedDeviceIds.length === 0) {
      message.warning('Please select at least one device');
      return;
    }

    if (activeStep === 2) {
      // Save on the last input step
      handleSave();
      return;
    }

    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate(-1);
      return;
    }
    setActiveStep(prev => prev - 1);
  };

  // Step 1: Name Access Group
  const renderNameStep = () => {
    return (
      <Box sx={{p: 3, maxWidth: 600, mx: 'auto'}}>
        <Typography variant="h6" sx={{mb: 3}}>
          {groupId ? 'Edit access group' : 'Create a new access group'}
        </Typography>

        <TextField
          label="Access Group Name"
          fullWidth
          required
          value={accessGroup.name}
          onChange={event => {
            setAccessGroup({...accessGroup, name: event.target.value});
          }}
          placeholder="Enter access group name"
          variant="outlined"
          sx={{mb: 2}}
        />

        <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
          An access group defines which devices users can access and when they can do so.
        </Typography>
      </Box>
    );
  };

  // Step 2: Select Devices
  const renderDeviceSelectionStep = () => {
    return (
      <Box sx={{p: 3}}>
        <Typography variant="h6" sx={{mb: 3}}>
          {groupId ? `Select Devices for "${accessGroup.name}"` : 'Select Devices'}
        </Typography>

        <DeviceSelectionTable
          devices={devices}
          selectedDeviceIds={selectedDeviceIds}
          onSelectionChange={setSelectedDeviceIds}
          loading={loadingDevices}
        />

        <Typography variant="body2" sx={{mt: 2}}>
          Selected {selectedDeviceIds.length} device(s)
        </Typography>
      </Box>
    );
  };

  // Step 3: Configure Access Times
  const renderAccessTimesStep = () => {
    return (
      <Box sx={{p: 3, maxWidth: 800, mx: 'auto'}}>
        <Typography variant="h6" sx={{mb: 3}}>
          {groupId ? 'Configure Access Settings' : 'Configure Access Settings'}
        </Typography>

        <Card sx={{mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2}}>
          <Typography variant="subtitle1" sx={{mb: 2}}>
            Access Settings (Applied to all selected devices)
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bulkAllTimeAccess}
                    onChange={e => setBulkAllTimeAccess(e.target.checked)}
                  />
                }
                label="All time access"
              />
            </Grid>

            {!bulkAllTimeAccess && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{mt: 1, mb: 2}}>
                  Access Time Range
                </Typography>

                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} sx={{mb: 3}}>
                  <TimePicker
                    label="Start Time"
                    value={bulkTimeStart}
                    onChange={newValue => setBulkTimeStart(newValue)}
                    slotProps={{textField: {variant: 'outlined', fullWidth: true}}}
                  />

                  <TimePicker
                    label="End Time"
                    value={bulkTimeEnd}
                    onChange={newValue => setBulkTimeEnd(newValue)}
                    slotProps={{textField: {variant: 'outlined', fullWidth: true}}}
                  />
                </Stack>

                <Typography variant="body2" sx={{mb: 1}}>
                  Days of Week
                </Typography>

                <Box sx={{mb: 3}}>
                  {WEEK_ITEMS.map((item, index) => (
                    <Chip
                      key={`bulk-${item.val}-${index}`}
                      label={item.label}
                      onClick={() => {
                        const isSelected = bulkWeekdays.includes(item.val);
                        const newBulkWeekdays = isSelected
                          ? bulkWeekdays.filter(day => day !== item.val)
                          : [...bulkWeekdays, item.val];
                        setBulkWeekdays(newBulkWeekdays);
                      }}
                      color={bulkWeekdays.includes(item.val) ? 'primary' : 'default'}
                      sx={{m: 0.5}}
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </Card>

        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Typography variant="body2" color="text.secondary">
            These settings will be applied to all {selectedDeviceIds.length} selected devices.
          </Typography>
        </Box>
      </Box>
    );
  };

  // Step 4: Done
  const renderDoneStep = () => {
    return (
      <Box sx={{p: 5, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <CheckCircleIcon color="success" sx={{fontSize: 80, mb: 2}} />

        <Typography variant="h5" sx={{mb: 2}}>
          {groupId ? 'Access Group Updated Successfully' : 'Access Group Created Successfully'}
        </Typography>

        <Typography variant="body1" sx={{mb: 4, textAlign: 'center'}}>
          {groupId
            ? `You've updated access group "${accessGroup.name}" with ${selectedDeviceIds.length} devices.`
            : `You've created a new access group "${accessGroup.name}" with ${selectedDeviceIds.length} devices.`}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/devices/accessGroup')}
          sx={{minWidth: 200}}
        >
          View All Access Groups
        </Button>
      </Box>
    );
  };

  // Render the appropriate step content
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderNameStep();
      case 1:
        return renderDeviceSelectionStep();
      case 2:
        return renderAccessTimesStep();
      case 3:
        return renderDoneStep();
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{width: '100%'}}>
      <Spin spinning={saving} />

      <Box sx={{display: 'flex', alignItems: 'center', mb: 3}}>
        <IconButton onClick={handleBack} sx={{mr: 1}}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          {groupId ? `Edit Access Group: ${accessGroup.name}` : 'Create Access Group'}
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{mb: 4}}>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper elevation={2} sx={{mb: 3, overflow: 'hidden'}}>
        {getStepContent(activeStep)}
      </Paper>

      <Box sx={{display: 'flex', justifyContent: 'flex-end', pt: 2}}>
        {activeStep < 3 && (
          <Button variant="contained" onClick={handleNext} disabled={saving} sx={{mt: 2}}>
            {activeStep === 2 ? (groupId ? 'Update Access Group' : 'Create Access Group') : 'Next'}
          </Button>
        )}
      </Box>
    </Box>
  );
}

interface DeviceCardProps {
  accessGroupIntercom: AccessGroupIntercom;
  intercomOptions: AutocompleteOption[];
  onRemoveIntercom: (accessGroupIntercom: AccessGroupIntercom) => void;
  onUpdateIntercom: (accessGroupIntercom: AccessGroupIntercom) => void;
}

function DeviceCard({
  intercomOptions,
  accessGroupIntercom,
  onRemoveIntercom,
  onUpdateIntercom,
}: DeviceCardProps) {
  const {weekdays = [], allTimeAccess, timeStart, timeEnd, intercomDeviceId} = accessGroupIntercom;

  const handleSelectIntercom = (e: any, val: AutocompleteVal) => {
    onUpdateIntercom({...accessGroupIntercom, intercomDeviceId: val?.value});
  };

  const handleSelectDay = (item: AccessGroupIntercomWeekdaysEnum) => {
    const targetIndex = weekdays!.indexOf(item);
    if (targetIndex !== -1) {
      const tmp = [...weekdays!];
      tmp.splice(targetIndex, 1);
      onUpdateIntercom({...accessGroupIntercom, weekdays: tmp});
    } else {
      onUpdateIntercom({...accessGroupIntercom, weekdays: [...weekdays!, item]});
    }
  };

  const handleAllTimeAccessChecked = (e: any, val: boolean) => {
    onUpdateIntercom({...accessGroupIntercom, allTimeAccess: val});
  };

  const handleTimeStartChange = (newValue: any) => {
    onUpdateIntercom({...accessGroupIntercom, timeStart: formatTime(newValue.$d)});
  };

  const handleTimeEndChange = (newValue: any) => {
    onUpdateIntercom({...accessGroupIntercom, timeEnd: formatTime(newValue.$d)});
  };

  const selectedIntercomValue = useMemo(() => {
    return intercomOptions.filter(_ => _.value === intercomDeviceId)[0] || null;
  }, [intercomOptions, intercomDeviceId]);

  return (
    <Card sx={{bgcolor: '#F7F7F7', borderRadius: '16px'}}>
      <CardContent>
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
          <Typography variant="subtitle1">
            {selectedIntercomValue?.label || 'Select a device'}
          </Typography>
          <IconButton onClick={() => onRemoveIntercom(accessGroupIntercom)}>
            <DeleteIcon />
          </IconButton>
        </Box>

        <Autocomplete
          size="small"
          options={intercomOptions}
          renderInput={params => (
            <TextField {...params} label="Select a device" variant="outlined" size="small" />
          )}
          value={selectedIntercomValue}
          onChange={handleSelectIntercom}
          sx={{mb: 2}}
        />

        <Divider sx={{my: 1}} />

        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <FormControlLabel
            control={<Checkbox checked={allTimeAccess} onChange={handleAllTimeAccessChecked} />}
            label="All time access"
          />
        </Box>

        {!allTimeAccess && (
          <>
            <Stack direction="row" spacing={2} sx={{mt: 2}}>
              <TimePicker
                label="Start"
                value={timeStart ? reverseFormatTime(timeStart) : null}
                slotProps={{textField: {variant: 'outlined', size: 'small'}}}
                onAccept={handleTimeStartChange}
              />
              <TimePicker
                label="End"
                value={timeEnd ? reverseFormatTime(timeEnd) : null}
                slotProps={{textField: {variant: 'outlined', size: 'small'}}}
                onAccept={handleTimeEndChange}
              />
            </Stack>

            <Typography variant="body2" sx={{mt: 2, mb: 1}}>
              Days of week
            </Typography>
            <Box>
              {WEEK_ITEMS.map((item, index) => (
                <Chip
                  key={`${item}-${index}`}
                  label={item.label}
                  onClick={() => handleSelectDay(item.val)}
                  color={(weekdays || []).includes(item.val) ? 'primary' : 'default'}
                  sx={{m: 0.5}}
                />
              ))}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
